import apiClient from './api'
import type {
  PaymentMethod,
  PaymentGateway,
  PaymentReconciliationRequest,
  PaymentReconciliationResponse
} from '../types/payment'

export interface PaymentTransactionCreate {
  amount: number
  payment_method: PaymentMethod
  payment_gateway?: PaymentGateway
  reference_number?: string
  notes?: string
  auto_complete?: boolean
}

export interface PaymentTransaction {
  id: string
  order_id: string
  customer_id: string
  transaction_number: string
  external_transaction_id?: string
  transaction_type: string
  amount: number
  currency: string
  payment_method: PaymentMethod
  payment_gateway?: PaymentGateway
  status: string
  gateway_response?: Record<string, any>
  gateway_transaction_id?: string
  transaction_date: string
  completed_at?: string
  failed_at?: string
  failure_reason?: string
  recorded_by_admin_id?: string
  reference_number?: string
  notes?: string
  transaction_metadata?: Record<string, any>
  created_at: string
  updated_at: string
  recorded_by_admin?: {
    id: string
    name: string
    email: string
  }
}

export interface OrderBalance {
  order_id: string
  order_number: string
  total_amount: number
  paid_amount: number
  remaining_balance: number
  payment_status: string
  payment_due_date?: string
  credit_terms_days: number
  transaction_count: number
  transactions: PaymentTransaction[]
}

export interface AgingReport {
  customer_id: string
  customer_name?: string
  customer_email?: string
  total_outstanding: number
  overdue_amount: number
  aging_buckets: {
    aging_0_30_days: number
    aging_31_60_days: number
    aging_61_90_days: number
    aging_over_90_days: number
  }
  calculated_at: string
}

export interface AgingReportList {
  reports: AgingReport[]
  total_customers: number
  total_outstanding: number
  total_overdue: number
  generated_at: string
}

export interface CreditAccount {
  customer_id: string
  customer_name?: string
  customer_email?: string
  credit_limit: number
  available_credit: number
  total_outstanding: number
  overdue_amount: number
  credit_utilization_percent: number
  default_payment_terms_days: number
  credit_hold: boolean
  credit_hold_reason?: string
  credit_hold_date?: string
  aging_buckets: {
    aging_0_30_days: number
    aging_31_60_days: number
    aging_61_90_days: number
    aging_over_90_days: number
  }
  last_payment_date?: string
  last_payment_amount: number
  created_at: string
  updated_at: string
}

export interface CreditUpdate {
  credit_limit?: number
  payment_terms_days?: number
  reason?: string
}

export interface CreditHoldRequest {
  reason: string
  notes?: string
}

export interface CreditHoldReleaseRequest {
  notes?: string
}

export interface PaymentDashboardMetrics {
  total_collected: number
  online_collected: number
  offline_collected: number
  today_collected: number
  total_outstanding: number
  total_payments_count: number
  unpaid_invoices_count: number
  active_links_count: number
}

export interface UnpaidInvoiceItem {
  id: string
  invoice_number: string
  invoice_date?: string
  due_date?: string
  customer_id: string
  customer_name: string
  customer_phone?: string
  total_amount: number
  paid_amount: number
  outstanding_amount: number
  status: string
  razorpay_payment_link_id?: string
  razorpay_payment_link_url?: string
  razorpay_payment_link_status?: string
}

export interface UnpaidInvoicesResponse {
  invoices: UnpaidInvoiceItem[]
  total: number
}

export interface RazorpaySyncResult {
  synced_count: number
  new_payments_created: number
  invoices_reconciled: number
  total_synced_amount: number
  items: Array<{
    payment_id: string
    amount: number
    status: string
    method?: string
    invoice_number?: string
    customer_contact?: string
    customer_email?: string
    date: string
  }>
}

export interface PaymentRecordRequest {
  invoice_id: string
  payment_date: string
  payment_method: string
  amount: number
  reference_number?: string
  bank_name?: string
  notes?: string
}

export interface PaymentListFilters {
  invoice_id?: string
  customer_id?: string
  payment_method?: string
  status?: string
  payment_date_from?: string
  payment_date_to?: string
  search_term?: string
  page?: number
  per_page?: number
}

export interface PaymentRecordItem {
  id: string
  payment_number: string
  invoice_id?: string | null
  customer_id?: string | null
  payment_date: string
  payment_method: string
  amount: number
  status: string
  reference_number?: string
  bank_name?: string
  notes?: string
  razorpay_payment_id?: string
  razorpay_order_id?: string
  created_at: string
  created_by_admin_id?: string
  invoice_number?: string
  customer_name?: string
}

export interface PaymentListResult {
  payments: PaymentRecordItem[]
  total: number
  page: number
  per_page: number
}

export interface PaymentLinkCreatePayload {
  invoice_id: string
  custom_expiry_hours?: number
  description?: string
  notes?: Record<string, any>
}

export const paymentService = {
  // Get dashboard aggregated KPIs
  getDashboardMetrics: async (): Promise<PaymentDashboardMetrics> => {
    const response = await apiClient.get('/admin/payments/dashboard/metrics')
    return response.data
  },

  // Get unpaid / pending invoices for payment collection & payment links
  getUnpaidInvoices: async (search?: string, limit = 100): Promise<UnpaidInvoicesResponse> => {
    const response = await apiClient.get('/admin/payments/unpaid-invoices', {
      params: { search, limit }
    })
    return response.data
  },

  // Pull / Sync all captured payments from Razorpay API
  syncRazorpayPayments: async (limit = 50): Promise<RazorpaySyncResult> => {
    const response = await apiClient.post('/admin/payments/sync-razorpay', null, {
      params: { limit }
    })
    return response.data
  },

  // List payments with filters & pagination
  listPayments: async (filters: PaymentListFilters = {}): Promise<PaymentListResult> => {
    const response = await apiClient.get('/admin/payments', { params: filters })
    return response.data
  },

  // Get payment detail
  getPaymentById: async (paymentId: string): Promise<PaymentRecordItem> => {
    const response = await apiClient.get(`/admin/payments/${paymentId}`)
    return response.data
  },

  // Record a payment against an invoice
  recordInvoicePayment: async (paymentData: PaymentRecordRequest): Promise<PaymentRecordItem> => {
    const response = await apiClient.post('/admin/payments', paymentData)
    return response.data
  },

  // Record an advance payment against a sales order (auto-creates draft invoice if needed)
  recordAdvancePayment: async (orderId: string, paymentData: {
    amount: number;
    payment_method: string;
    payment_date: string;
    reference_number?: string;
    bank_name?: string;
    notes?: string;
  }): Promise<{ success: boolean; payment_number: string; invoice_id: string; auto_created_invoice: boolean; message: string }> => {
    const response = await apiClient.post(`/admin/sales-orders/${orderId}/advance-payment`, paymentData)
    return response.data
  },

  // Get Razorpay payment link status for an invoice
  getPaymentLinkStatus: async (invoiceId: string): Promise<{ status: string; paid_amount?: number; payment_link_url?: string }> => {
    const response = await apiClient.get(`/admin/razorpay/payment-links/invoice/${invoiceId}/status`)
    return response.data
  },

  // Void a payment
  voidPayment: async (paymentId: string, void_reason: string, notes?: string): Promise<{ success: boolean; message: string }> => {
    const response = await apiClient.post(`/admin/payments/${paymentId}/void`, {
      void_reason,
      notes
    })
    return response.data
  },

  // Generate a Razorpay payment link for an invoice
  generatePaymentLink: async (payload: PaymentLinkCreatePayload): Promise<any> => {
    const response = await apiClient.post('/admin/razorpay/payment-links', payload)
    return response.data
  },

  // Cancel a Razorpay payment link
  cancelPaymentLink: async (invoiceId: string): Promise<any> => {
    const response = await apiClient.post(`/admin/razorpay/payment-links/invoice/${invoiceId}/cancel`)
    return response.data
  },

  // Record payment for an order (legacy/order-based)
  recordPayment: async (orderId: string, paymentData: PaymentTransactionCreate): Promise<PaymentTransaction> => {
    const response = await apiClient.post(`/admin/orders/${orderId}/payments`, paymentData)
    return response.data
  },

  // Get payment history for an order
  getOrderPayments: async (orderId: string): Promise<PaymentTransaction[]> => {
    const response = await apiClient.get(`/admin/orders/${orderId}/payments`)
    return response.data
  },

  // Get order balance
  getOrderBalance: async (orderId: string): Promise<OrderBalance> => {
    const response = await apiClient.get(`/admin/orders/${orderId}/balance`)
    return response.data
  },

  // Payment reconciliation
  reconcilePayments: async (request: PaymentReconciliationRequest): Promise<PaymentReconciliationResponse> => {
    const response = await apiClient.post('/admin/payments/reconcile', request)
    return response.data
  },

  // Get aging report
  getAgingReport: async (): Promise<AgingReportList> => {
    const response = await apiClient.get('/admin/payments/aging-report')
    return response.data
  },

  // Get customer credit account
  getCustomerCredit: async (customerId: string): Promise<CreditAccount> => {
    const response = await apiClient.get(`/admin/customers/${customerId}/credit`)
    return response.data
  },

  // Update customer credit
  updateCustomerCredit: async (customerId: string, creditData: CreditUpdate): Promise<CreditAccount> => {
    const response = await apiClient.patch(`/admin/customers/${customerId}/credit`, creditData)
    return response.data
  },

  // Apply credit hold
  applyCreditHold: async (customerId: string, holdData: CreditHoldRequest): Promise<any> => {
    const response = await apiClient.post(`/admin/customers/${customerId}/credit/hold`, holdData)
    return response.data
  },

  // Release credit hold
  releaseCreditHold: async (customerId: string, releaseData: CreditHoldReleaseRequest): Promise<any> => {
    const response = await apiClient.post(`/admin/customers/${customerId}/credit/release`, releaseData)
    return response.data
  },

  // Capture an authorized Razorpay payment
  captureRazorpayPayment: async (razorpayPaymentId: string): Promise<{ success: boolean; payment_id: string; status: string; amount: number; message: string }> => {
    const response = await apiClient.post(`/admin/razorpay/payments/${razorpayPaymentId}/capture`)
    return response.data
  },

  // Export aging report to CSV
  exportAgingReport: async (): Promise<void> => {
    const response = await apiClient.get('/admin/payments/aging-report/export', {
      responseType: 'blob'
    })

    const blob = new Blob([response.data], { type: 'text/csv' })
    const url = window.URL.createObjectURL(blob)
    const link = document.createElement('a')
    link.href = url
    link.download = `aging_report_${new Date().toISOString().split('T')[0]}.csv`
    document.body.appendChild(link)
    link.click()
    document.body.removeChild(link)
    window.URL.revokeObjectURL(url)
  }
}
